/**
 * packageExpansion.js  (WP-1)
 *
 * Pure, side-effect-free expansion of embedded package nodes into a flat
 * host graph, run just before descriptor generation. A package node lives
 * permanently in the designer_payload (author sees the 📦 reference); only
 * the compiled descriptor sees the expanded contents.
 *
 * Public API:
 *   expandPackageNodes(nodes, edges) -> { nodes, edges }
 *
 * For each node of type 'package' (in stable array order):
 *   1. Read data.packageContent = { nodes, edges } (the frozen internal graph).
 *   2. Apply a per-instance C3 namespace prefix (`${instanceName}__`) to every
 *      internal node id, variable name, stimulus_id, and to occurrences of
 *      internal variables inside internal edge conditions.
 *   3. Drop the internal Start/End nodes, recording their attachment points
 *      (what Start pointed to = entries; what pointed to End = exits).
 *   4. Rewire host edges: inbound edges retarget to entries; outbound edges
 *      re-source from exits. Host edge data (condition/priority) is preserved.
 *      With k exits and m outbound host edges, k×m edges are emitted.
 *   5. Stamp provenance (package_id, package_version, package_instance) on every
 *      spliced node (C5) so the descriptor builder can emit it.
 *
 * Expansion assumes §3.9 self-containment as a precondition (enforced at
 * Save as Package). It re-checks only structural minimums and throws with a
 * specific message otherwise.
 *
 * Conventions honoured:
 *   C3 — prefix is `${instanceName}__` (double underscore), alpha-first.
 *   C4 — no output node id matches /^start/i or /^end[_-]/i (asserted in tests).
 *   C5 — provenance fields on every expanded node.
 * The word-boundary rule for variable substitution matches variableScanner.js:
 *   boundary = start/end of string or a char in [^A-Za-z0-9_-].
 */

// ────────────────────────────────────────────────────────────────────────
// Small helpers
// ────────────────────────────────────────────────────────────────────────

/** Deep clone plain graph data (packageContent is JSON-serializable). */
function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

/** Escape a string for literal use inside a RegExp (same as variableScanner). */
function escapeReg(s = '') {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replace exact-word occurrences of each internal variable name inside a
 * condition string with its prefixed form. Word boundary = string edge or a
 * char in [^A-Za-z0-9_-] (variableScanner convention). Because prefixed names
 * are delimited by `_` (not a boundary char), replacements never re-match, so
 * substitution order is irrelevant.
 */
function substituteConditionVars(condition, internalVars, prefix) {
  if (typeof condition !== 'string' || condition === '') return condition;
  let out = condition;
  internalVars.forEach((name) => {
    if (!name) return;
    const re = new RegExp(`(^|[^A-Za-z0-9_-])${escapeReg(name)}(?=$|[^A-Za-z0-9_-])`, 'g');
    out = out.replace(re, (_m, boundary) => `${boundary}${prefix(name)}`);
  });
  return out;
}

/** Collect variable names defined by a package's internal question nodes. */
function collectInternalVariables(internalNodes) {
  const vars = new Set();
  internalNodes.forEach((n) => {
    const d = n.data || {};
    const v = d.variable || d.variable_name;
    if (v && String(v).trim() !== '') vars.add(String(v));
  });
  return vars;
}

// ────────────────────────────────────────────────────────────────────────
// Per-instance expansion
// ────────────────────────────────────────────────────────────────────────

/**
 * Expand a single package node into namespaced internal nodes/edges plus its
 * boundary attachment points (entries/exits, already prefixed).
 *
 * @returns {{ internalNodes, internalEdges, entries: string[], exits: string[] }}
 */
function expandOne(pkg) {
  const data = pkg.data || {};
  const content = data.packageContent;

  if (!content || !Array.isArray(content.nodes) || !Array.isArray(content.edges)) {
    throw new Error(`Package node "${pkg.id}" has no packageContent to expand.`);
  }

  const instanceName = data.instanceName || pkg.id;
  const prefix = (s) => `${instanceName}__${s}`;

  const iNodes = content.nodes;
  const iEdges = content.edges;

  // Reject nested packages (v1 no-nesting rule, §3.1).
  if (iNodes.some((n) => n.type === 'package')) {
    throw new Error(`Package "${instanceName}" contains a nested package node — nesting is not supported.`);
  }

  // Require exactly one Start and one End (structural minimum).
  const startNodes = iNodes.filter((n) => n.type === 'start');
  const endNodes = iNodes.filter((n) => n.type === 'end');
  if (startNodes.length !== 1) {
    throw new Error(`Package "${instanceName}" must contain exactly one Start node (found ${startNodes.length}).`);
  }
  if (endNodes.length !== 1) {
    throw new Error(`Package "${instanceName}" must contain exactly one End node (found ${endNodes.length}).`);
  }
  const startId = startNodes[0].id;
  const endId = endNodes[0].id;

  const internalVars = collectInternalVariables(iNodes);

  const isBoundary = (id) => id === startId || id === endId;

  // Attachment points, deduped, prefixed. entries = what Start pointed to;
  // exits = what pointed to End.
  const entries = [...new Set(
    iEdges.filter((e) => e.source === startId).map((e) => prefix(e.target))
  )];
  const exits = [...new Set(
    iEdges.filter((e) => e.target === endId).map((e) => prefix(e.source))
  )];

  // Namespace the real (non-Start/End) internal nodes.
  const internalNodes = iNodes
    .filter((n) => !isBoundary(n.id))
    .map((n) => {
      const clone = deepClone(n);
      clone.id = prefix(n.id);
      const d = clone.data || (clone.data = {});
      if (d.variable) d.variable = prefix(d.variable);
      if (d.variable_name) d.variable_name = prefix(d.variable_name);
      if (d.stimulus_id) d.stimulus_id = prefix(d.stimulus_id);
      // Provenance (C5) — descriptor builder passes these through when present.
      d.package_id = data.packageId ?? null;
      d.package_version = data.packageVersion ?? null;
      d.package_instance = instanceName;
      return clone;
    });

  // Namespace fully-internal edges (both endpoints are real nodes). Edges that
  // touch Start/End are dropped — their connectivity is carried by entries/exits.
  const internalEdges = iEdges
    .filter((e) => !isBoundary(e.source) && !isBoundary(e.target))
    .map((e) => {
      const clone = deepClone(e);
      clone.id = prefix(e.id != null ? String(e.id) : `${e.source}->${e.target}`);
      clone.source = prefix(e.source);
      clone.target = prefix(e.target);
      if (clone.data && typeof clone.data.condition === 'string') {
        clone.data.condition = substituteConditionVars(clone.data.condition, internalVars, prefix);
      }
      return clone;
    });

  return { internalNodes, internalEdges, entries, exits };
}

// ────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────

/**
 * Expand every package node in a host graph into flat, namespaced content.
 *
 * @param {Array} nodes - React Flow nodes (host graph)
 * @param {Array} edges - React Flow edges (host graph)
 * @returns {{ nodes: Array, edges: Array }} expanded graph. When there are no
 *   package nodes the inputs are returned unchanged (same references).
 */
function expandPackageNodes(nodes, edges) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeEdges = Array.isArray(edges) ? edges : [];

  const pkgNodes = safeNodes.filter((n) => n.type === 'package');
  if (pkgNodes.length === 0) {
    // Idempotent no-op — return inputs unchanged.
    return { nodes, edges };
  }

  const pkgIds = new Set(pkgNodes.map((n) => n.id));

  // Boundary maps: package node id -> its entry / exit node ids (prefixed).
  const entryNodes = new Map();
  const exitNodes = new Map();

  const outNodes = [];
  const internalEdgesAll = [];

  // Carry through non-package host nodes (unchanged references — never mutated).
  safeNodes.forEach((n) => {
    if (n.type !== 'package') outNodes.push(n);
  });

  // Expand each package instance in stable order.
  pkgNodes.forEach((pkg) => {
    const { internalNodes, internalEdges, entries, exits } = expandOne(pkg);
    outNodes.push(...internalNodes);
    internalEdgesAll.push(...internalEdges);
    entryNodes.set(pkg.id, entries);
    exitNodes.set(pkg.id, exits);
  });

  // Rewire host edges, resolving any package endpoint to its boundary nodes.
  // A plain host edge (neither endpoint a package) passes through unchanged.
  let rewireSeq = 0;
  const freshEdgeId = () => `pkgexp_e${rewireSeq++}`;
  const outEdges = [...internalEdgesAll];

  safeEdges.forEach((e) => {
    const srcIsPkg = pkgIds.has(e.source);
    const tgtIsPkg = pkgIds.has(e.target);

    if (!srcIsPkg && !tgtIsPkg) {
      outEdges.push(e);
      return;
    }

    const sources = srcIsPkg ? exitNodes.get(e.source) : [e.source];
    const targets = tgtIsPkg ? entryNodes.get(e.target) : [e.target];

    // k×m fan-out, preserving the host edge's data (condition/priority).
    sources.forEach((s) => {
      targets.forEach((t) => {
        outEdges.push({
          ...e,
          id: freshEdgeId(),
          source: s,
          target: t,
          data: e.data ? { ...e.data } : e.data,
        });
      });
    });
  });

  return { nodes: outNodes, edges: outEdges };
}

module.exports = { expandPackageNodes };
module.exports.default = expandPackageNodes;
